import { useActionState, useOptimistic } from 'react';
import type { ActionResult } from '../utils/formActions';

/**
 * Options for useFormAction hook
 */
interface UseFormActionOptions<T> {
  onSuccess?: (data: T) => void | Promise<void>;
  onError?: (error: string) => void;
  optimisticUpdate?: boolean;
  resetOnSuccess?: boolean;
}

/**
 * Enhanced hook for form actions with common patterns
 * @param action - The form action function
 * @param initialState - Initial state
 * @param options - Additional options
 */
export function useFormAction<T = unknown>(
  action: (prevState: ActionResult<T>, formData: FormData) => Promise<ActionResult<T>>,
  initialState: ActionResult<T> = {},
  options: UseFormActionOptions<T> = {}
) {
  const [state, formAction, isPending] = useActionState(
    async (prevState: ActionResult<T>, formData: FormData) => {
      console.log('🎯 [useFormAction] Wrapper function called', { prevState, formData: Object.fromEntries(formData.entries()) });
      try {
        console.log('🎯 [useFormAction] Calling action function...');
        const result = await action(prevState, formData);
        
        // Handle success callback
        if (result.success && result.data && options.onSuccess) {
          await options.onSuccess(result.data);
        }
        
        // Handle error callback
        if (result.error && options.onError) {
          options.onError(result.error);
        }
        
        return result;
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred';
        if (options.onError) {
          options.onError(errorMessage);
        }
        return { error: errorMessage };
      }
    },
    initialState
  );

  return {
    state,
    formAction,
    isPending,
    error: state.error,
    errors: state.errors,
    success: state.success,
    data: state.data
  };
}

/**
 * Hook for form actions with optimistic updates
 * @param action - The form action function
 * @param currentValue - Current value for optimistic updates
 * @param options - Additional options
 */
export function useOptimisticFormAction<T>(
  action: (prevState: ActionResult<T>, formData: FormData) => Promise<ActionResult<T>>,
  currentValue: T,
  options: UseFormActionOptions<T> = {}
) {
  const [optimisticValue, setOptimisticValue] = useOptimistic(currentValue);
  
  const { state, formAction, isPending } = useFormAction(
    async (prevState, formData) => {
      // Extract expected value from form data for optimistic update
      const expectedValue = extractOptimisticValue<T>(formData);
      if (expectedValue !== undefined) {
        setOptimisticValue(expectedValue);
      }
      
      const result = await action(prevState, formData);
      
      // Revert optimistic update on error
      if (result.error) {
        setOptimisticValue(currentValue);
      }
      
      return result;
    },
    {},
    options
  );

  return {
    state,
    formAction,
    isPending,
    optimisticValue,
    error: state.error,
    errors: state.errors,
    success: state.success,
    data: state.data
  };
}

/**
 * Helper to extract optimistic value from form data
 * Override this function based on your form structure
 */
function extractOptimisticValue<T>(formData: FormData): T | undefined {
  // This is a placeholder - in real usage, you'd extract the relevant
  // data from formData based on your form structure
  const value = formData.get('value');
  return value as T | undefined;
}

/**
 * Hook for simple form submissions without complex state
 * @param handler - The async handler function
 */
export function useSimpleFormAction<T = void>(
  handler: (formData: FormData) => Promise<T>
) {
  return useFormAction<T>(
    async (_, formData) => {
      try {
        const result = await handler(formData);
        return { success: true, data: result };
      } catch (error) {
        return { 
          error: error instanceof Error ? error.message : 'An unexpected error occurred' 
        };
      }
    }
  );
}